const DESCRIPTION_MAX_LENGTH = 400;
const DOCUMENT_NODE = 9;

function isDocument(value) {
  return !!(value && value.nodeType === DOCUMENT_NODE && value.documentElement);
}

function hasParserError(xmlDoc) {
  if (!xmlDoc || !xmlDoc.getElementsByTagName) return true;
  try {
    return xmlDoc.getElementsByTagName('parsererror').length > 0;
  } catch (error) {
    return true;
  }
}

function firstElementByNames(parent, names) {
  if (!parent || !parent.getElementsByTagName || !names) return null;

  for (let i = 0; i < names.length; i++) {
    try {
      const nodes = parent.getElementsByTagName(names[i]);
      if (nodes && nodes.length > 0) return nodes[0];
    } catch (error) {
      // Some WebKit builds throw on namespaced tag lookups (e.g. content:encoded).
    }
  }

  return firstElementByLocalName(parent, names);
}

function localNameOf(node) {
  if (!node) return '';
  if (node.localName) return node.localName;
  const nodeName = node.nodeName || '';
  const colon = nodeName.lastIndexOf(':');
  return colon === -1 ? nodeName : nodeName.substring(colon + 1);
}

function firstElementByLocalName(parent, names) {
  if (!parent || !parent.getElementsByTagName) return null;

  const wanted = {};
  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    const colon = name.lastIndexOf(':');
    wanted[colon === -1 ? name : name.substring(colon + 1)] = true;
  }

  let nodes;
  try {
    nodes = parent.getElementsByTagName('*');
  } catch (error) {
    return null;
  }

  for (let i = 0; i < nodes.length; i++) {
    if (wanted[localNameOf(nodes[i])]) return nodes[i];
  }

  return null;
}

export function stripHtmlAndDecode(html) {
  if (html == null) return '';
  const asString = typeof html === 'string' ? html : String(html);
  if (!asString) return '';

  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = asString;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  } catch (error) {
    return asString.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function elementText(el) {
  if (!el) return '';
  return stripHtmlAndDecode(el.textContent || el.text || '');
}

function truncateDescription(text) {
  if (!text || text.length <= DESCRIPTION_MAX_LENGTH) return text || '';
  return text.substring(0, DESCRIPTION_MAX_LENGTH) + '...';
}

function looksLikeImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(url);
}

function isImageElement(el) {
  if (!el || !el.getAttribute) return false;
  const type = el.getAttribute('type') || '';
  const medium = el.getAttribute('medium') || '';
  const url = el.getAttribute('url') || '';
  if (medium === 'image') return true;
  if (type.indexOf('image/') === 0) return true;
  return looksLikeImageUrl(url);
}

function extractLink(item) {
  const links = [];
  try {
    const nodes = item.getElementsByTagName('link');
    for (let i = 0; i < nodes.length; i++) {
      links.push(nodes[i]);
    }
  } catch (error) {
    return '';
  }

  let fallback = '';
  for (let i = 0; i < links.length; i++) {
    const el = links[i];
    const href = el.getAttribute ? el.getAttribute('href') : '';
    const rel = el.getAttribute ? el.getAttribute('rel') : '';
    const text = elementText(el);

    if (rel === 'alternate' && href) return href;
    if (!fallback) {
      fallback = href || text;
    }
  }

  return fallback;
}

function extractDescription(item) {
  const encodedText = elementText(firstElementByNames(item, ['content:encoded', 'encoded']));
  if (encodedText) return truncateDescription(encodedText);

  const descriptionText = elementText(firstElementByNames(item, ['description']));
  if (descriptionText) return truncateDescription(descriptionText);

  const summaryText = elementText(firstElementByNames(item, ['summary']));
  if (summaryText) return truncateDescription(summaryText);

  const contentEl = firstElementByNames(item, ['content']);
  // Atom <content> has body text; media:content is an image tag with a url.
  if (contentEl && !contentEl.getAttribute('url')) {
    return truncateDescription(elementText(contentEl));
  }

  return '';
}

function extractThumbnail(item) {
  const enclosure = firstElementByNames(item, ['enclosure']);
  if (enclosure && isImageElement(enclosure)) {
    return enclosure.getAttribute('url') || '';
  }

  let nodes;
  try {
    nodes = item.getElementsByTagName('*');
  } catch (error) {
    return '';
  }

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const name = localNameOf(node);
    if (name !== 'content' && name !== 'thumbnail' && name !== 'enclosure') continue;
    if (isImageElement(node)) {
      return node.getAttribute('url') || '';
    }
  }

  return '';
}

function collectItems(xmlDoc) {
  const collected = [];
  const tagNames = ['item', 'entry'];

  for (let t = 0; t < tagNames.length; t++) {
    let nodes;
    try {
      nodes = xmlDoc.getElementsByTagName(tagNames[t]);
    } catch (error) {
      continue;
    }
    for (let i = 0; i < nodes.length; i++) {
      collected.push(nodes[i]);
    }
    if (collected.length > 0) return collected;
  }

  return collected;
}

function mapFeedItem(item) {
  const title = elementText(firstElementByNames(item, ['title']));
  const description = extractDescription(item);
  const pubDateEl = firstElementByNames(item, ['pubDate', 'published', 'updated', 'dc:date']);

  return {
    title: title,
    link: extractLink(item),
    pubDate: elementText(pubDateEl),
    description: description,
    thumbnail: extractThumbnail(item),
  };
}

/**
 * Parse RSS/Atom XML into a Document. Accepts a string or an existing Document.
 * Returns null for empty, invalid, or unparseable input.
 */
export function parseXml(data) {
  if (data == null || data === '') return null;

  if (isDocument(data)) {
    return hasParserError(data) ? null : data;
  }

  if (typeof data !== 'string') {
    console.error('Unexpected RSS data format:', typeof data, data);
    return null;
  }

  const rssText = data.replace(/^\uFEFF/, '').trim();
  if (!rssText) return null;

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(rssText, 'application/xml');
    if (!xmlDoc || hasParserError(xmlDoc)) {
      console.error('RSS XML parsererror');
      return null;
    }
    return xmlDoc;
  } catch (error) {
    console.error('Error parsing RSS XML:', error);
    return null;
  }
}

/**
 * Extract normalized feed items from a parsed RSS/Atom Document.
 * Empty / untitled-and-undescribed nodes are skipped.
 */
export function extractRssItems(xmlDoc) {
  if (!xmlDoc || hasParserError(xmlDoc)) return [];

  const nodes = collectItems(xmlDoc);
  const feedItems = [];

  for (let i = 0; i < nodes.length; i++) {
    try {
      const mapped = mapFeedItem(nodes[i]);
      if (mapped.title || mapped.description) {
        feedItems.push(mapped);
      }
    } catch (error) {
      console.error('Error extracting RSS item:', error);
    }
  }

  return feedItems;
}

/**
 * Parse a raw RSS/Atom payload (string or Document) into feed items.
 * Always returns an array.
 */
export function parseRssData(data) {
  try {
    const xmlDoc = parseXml(data);
    if (!xmlDoc) return [];
    return extractRssItems(xmlDoc);
  } catch (error) {
    console.error('Error parsing RSS data:', error);
    return [];
  }
}
